import { COLLECTION_ID } from "@highnote/server/src/core/entities";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { EntityRowConfig } from "App/components/EntityTable";
import {
  ACTION_COLUMNS,
  ENTITY_TYPE,
  MAIN_CONTENT_COLUMNS,
  UTILITY_COLUMNS,
} from "App/components/EntityTable/config";
import { EntityIconExpand } from "App/components/EntityTable/EntityIconExpand";
import { ReactComponent as DragHandleSVG } from "App/common/icons-v2/move-2.svg";
import { useIsInView } from "App/components/util";
import { AppCheckbox } from "App/core/Checkbox/AppCheckbox";
import { SORT_TYPE } from "App/routes/Main/Library/config";
import classNames from "classnames";
import React, { useMemo, useRef, useState } from "react";
import Measure, { ContentRect } from "react-measure";
import { useSetUtilityGutterWidth } from "./useUtilityGutterWidth";
import "./BaseTable.scss";
import { Skeleton } from "./Skeleton/Skeleton";
import { useTheme } from "./ThemeProvider";
import { useViewport } from "./useViewport";
import { DnDWrapper } from "App/components/DragAndDrop/DnDWrapper";
import { useEntitiesSelection } from "App/components/useEntitiesSelection";
import { ContextMenuActions } from "App/components/Actions/ContextMenuActions";
import type { MenuProps } from "@mui/material/Menu";

export function getEntityCollectionFromType(type: ENTITY_TYPE) {
  return type === ENTITY_TYPE.TRACK
    ? COLLECTION_ID.TRACK
    : type === ENTITY_TYPE.SPACE
      ? COLLECTION_ID.SPACE
      : COLLECTION_ID.FILE;
}

export type ColumnConfig = {
  name: string;
  id: string;
  Component?: React.FC<{ row: RowConfig }>;
  showOnHover?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RowConfig = Record<string, any> & {
  key?: string;
  attributes?: Record<string, string>;
  selected?: boolean;
  // New optional properties for nested row handling
  NestedTable?: React.FC<{
    parentRow: EntityRowConfig;
    tableRowProps: Omit<BaseTableRowProps, "row">;
    sortType?: SORT_TYPE;
  }>;
};

export type TableRowVariant =
  | "no-nesting"
  | "expanded-row"
  | "draggable-expanded-row"
  | "nested-click-thru";

type DraggableRowItem = {
  row: RowConfig;
  columns: ColumnConfig[];
  isLastRow?: boolean;
  isFirstRow?: boolean;
  onSelect?: (row: RowConfig) => void;
  tableRowVariant?: TableRowVariant;
  hideSelectionColumn?: boolean;
};

const DefaultColumnComponent = ({ row }: { row: RowConfig }) => {
  return <div>{JSON.stringify(row)}</div>;
};

// 64px white space: Expand button 34px, Column padding 2px * 2 = 4px, 16px for padding between columns
export const EXPAND_COLUMN_WIDTH = 34;
// 50px white space: Expand button 30px, Column padding 2px * 2 = 4px, 16px for padding between columns
export const EXPAND_COLUMN_WIDTH_MOBILE = 30;
const DEFAULT_INDENTATION = 16;
const DEFAULT_INDENTATION_MOBILE = 12;
export const calculateChildIndentation = (
  indentation: number,
  isMobile: boolean,
) => {
  return (
    indentation +
    (isMobile ? DEFAULT_INDENTATION_MOBILE : DEFAULT_INDENTATION) * 2
  );
};

export interface BaseTableRowProps {
  row: RowConfig;
  parentRow?: RowConfig;
  columns: ColumnConfig[];
  onSelect?: (row: RowConfig, parentEntity?: RowConfig) => void;
  // Enables dragging the row (via DnDWrapper). Behavioral only — renders nothing.
  enableDrag?: boolean;
  // Renders the visual drag-handle affordance in the utility gutter.
  // Presentational only — dragging still works without it (drag the row body).
  showDragHandle?: boolean;
  isLastRow?: boolean;
  // Only the first row measures its utility gutter, so a table whose rows have
  // differing gutters settles on one width instead of flipping between them.
  // Nested tables never set this, so nested rows never report.
  isFirstRow?: boolean;
  indentation?: number;
  tableRowVariant?: TableRowVariant;
  hideSelectionColumn?: boolean;
}

export const BaseRow = ({
  row,
  parentRow,
  columns = [],
  enableDrag,
  showDragHandle = false,
  isLastRow,
  isFirstRow,
  onSelect,
  indentation = 0,
  tableRowVariant,
  hideSelectionColumn,
}: BaseTableRowProps) => {
  const { isMobile } = useViewport();
  const setUtilityGutterWidth = useSetUtilityGutterWidth();
  const defaultColumnWidth = useMemo(
    () => (isMobile ? EXPAND_COLUMN_WIDTH_MOBILE : EXPAND_COLUMN_WIDTH),
    [isMobile],
  );

  const [isRowExpanded, setIsRowExpanded] = useState(false);
  const hasNestedRows =
    row?.attributes?.["data-is-nested"] === "true" || row.NestedTable;
  const isExpandableRow =
    tableRowVariant === "expanded-row" ||
    tableRowVariant === "draggable-expanded-row";

  const expandedColumn = {
    name: "Expand",
    id: "expand",
    showOnHover: false,
    Component: ({ row }: { row: EntityRowConfig }) => {
      if (isExpandableRow) {
        // TODO: For now, we are not showing the expanded rows on mobile for tracks/versions.
        if (
          isMobile &&
          (row.type === ENTITY_TYPE.TRACK ||
            row.type === ENTITY_TYPE.TRACK_VERSION)
        )
          return null;

        if (hasNestedRows) {
          return (
            <div style={{ paddingLeft: isMobile ? 0 : indentation }}>
              <EntityIconExpand
                row={row}
                isExpanded={isRowExpanded}
                toggleExpanded={toggleExpandRow}
              />
            </div>
          );
        }

        if (!isMobile) {
          return (
            <div
              className="entity-icon-expand"
              style={{
                // To keep the table layout consistent, return an
                // empty div the size of the EntityIconExpand button,
                // if the row is not expandable.
                width: defaultColumnWidth,
                height: 30,
                paddingLeft: indentation + defaultColumnWidth,
              }}
            />
          );
        }
      }

      return null;
    },
  };

  const toggleExpandRow = () => {
    setIsRowExpanded((prev) => {
      return !prev;
    });
  };

  const selectColumn = {
    name: "Check",
    id: "check",
    showOnHover: true,
    Component: ({ row }: { row: EntityRowConfig }) => {
      return (
        <AppCheckbox
          checked={!!row.selected}
          className="selection-checkbox"
          onClick={() => {
            onSelect(row, parentRow);
          }}
        />
      );
    },
  };

  const renderColumns = useMemo(() => {
    const utilityColumns = columns.filter((column) =>
      UTILITY_COLUMNS.includes(column.id),
    );

    // Drag-handle affordance — presentational only, and opt-in via
    // `showDragHandle`. Dragging itself works on the row body via DnDWrapper
    // (enabled by `enableDrag`/`onDragEnd`), so no handle or placeholder cell is
    // rendered unless a caller explicitly asks for the visual handle. Hidden at
    // or below M, where native HTML5 drag never fires.
    if (!isMobile && showDragHandle) {
      utilityColumns.unshift({
        name: "Drag To Reorder",
        id: "drag-handle",
        showOnHover: true,
        Component: () => (
          <div className="drag-handle" data-is-draggable={showDragHandle}>
            <DragHandleSVG />
          </div>
        ),
      });
    }

    if (!!onSelect && !hideSelectionColumn) {
      // Checkbox goes right after the drag handle when present, otherwise first.
      // e.g. [drag-handle?, checkbox, expand-trigger...]
      const checkboxIndex = showDragHandle && !isMobile ? 1 : 0;
      utilityColumns.splice(checkboxIndex, 0, selectColumn);
    }

    const mainContentColumns = columns.filter((column) =>
      MAIN_CONTENT_COLUMNS.includes(column.id),
    );

    // The expand toggle is desktop-only — below M we don't render the column at
    // all (no toggle, no reserved slot). Above M it goes at the end of the
    // utility gutter.
    if (isExpandableRow && !isMobile) {
      utilityColumns.push(expandedColumn);
    }

    const actionColumns = columns.filter((column) =>
      ACTION_COLUMNS.includes(column.id),
    );

    // Left utility columns section (drag handle, checkbox).
    const utilityCells = utilityColumns.map((column) => {
      const Cmp = column.Component || DefaultColumnComponent;
      return (
        <TableCell
          component={"div"}
          scope="row"
          data-column-id={column.id}
          data-show-on-hover={column.showOnHover}
          key={column.id}
          className="utility-column"
        >
          <Cmp row={row} />
        </TableCell>
      );
    });

    return (
      <>
        {isFirstRow ? (
          // Measured so content outside the table can indent to match.
          <Measure
            bounds
            onResize={(rect: ContentRect) => {
              if (rect.bounds) setUtilityGutterWidth(rect.bounds.width);
            }}
          >
            {({ measureRef }) => (
              <div ref={measureRef} className="row-utility-columns">
                {utilityCells}
              </div>
            )}
          </Measure>
        ) : (
          <div className="row-utility-columns">{utilityCells}</div>
        )}
        {/* Main content columns section (expand, preview, actions, etc.) */}
        <div
          className="row-content-columns"
          style={
            isMobile && indentation > 0
              ? { marginLeft: indentation }
              : undefined
          }
        >
          {mainContentColumns.map((column) => {
            const Cmp = column.Component || DefaultColumnComponent;
            return (
              <TableCell
                component={"div"}
                scope="row"
                data-column-id={column.id}
                data-show-on-hover={column.showOnHover}
                key={column.id}
                className="content-column"
              >
                <Cmp row={row} />
              </TableCell>
            );
          })}
          {actionColumns.map((column) => {
            const Cmp = column.Component || DefaultColumnComponent;
            return (
              <TableCell
                component={"div"}
                scope="row"
                data-column-id={column.id}
                data-show-on-hover={column.showOnHover}
                key={column.id}
                className="content-column"
              >
                <Cmp row={row} />
              </TableCell>
            );
          })}
        </div>
      </>
    );
  }, [columns, row, enableDrag, isRowExpanded, isExpandableRow, isMobile]);

  const renderNestedRows = () => {
    if (isRowExpanded && row.NestedTable) {
      return (
        <row.NestedTable
          parentRow={row as EntityRowConfig}
          tableRowProps={{
            columns,
            indentation: calculateChildIndentation(indentation, isMobile),
            tableRowVariant,
            onSelect,
          }}
        />
      );
    }
    return null;
  };

  const rowRef = useRef<HTMLDivElement>(null);

  const isInView = useIsInView(rowRef);

  const [contextMenuAnchorPosition, setContextMenuAnchorPosition] = useState<
    MenuProps["anchorPosition"] | null
  >(null);

  return (
    <div ref={rowRef} className="base-table-row-wrapper">
      <TableRow
        key={`${row.type}-${row.id}`}
        component={"div"}
        data-clickable={!!row.onClick}
        onClick={
          row.entireRowClickable && !!row.onClick && !isMobile
            ? row.onClick
            : undefined
        }
        aria-selected={!!row.selected}
        data-is-last-row={!!isLastRow}
        data-cypress-id={"base-table-row"}
        data-cypress-row-type={row.type}
        data-id={row.id}
        className={classNames({
          ["draggable-expanded-row"]:
            tableRowVariant === "draggable-expanded-row",
          ["show-cursor-icon"]:
            row.entireRowClickable && !!row.onClick && !isMobile,
        })}
        onContextMenu={(event) => {
          event.preventDefault();
          setContextMenuAnchorPosition(
            contextMenuAnchorPosition === null
              ? {
                  top: event.clientY - 6,
                  left: event.clientX + 2,
                }
              : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                // Other native context menus might behave different.
                // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                null,
          );
        }}
        {...(row.attributes || {})}
      >
        {isInView ? renderColumns : null}
      </TableRow>
      {isExpandableRow && isInView && renderNestedRows()}
      {contextMenuAnchorPosition && (
        <ContextMenuActions
          row={row as EntityRowConfig}
          anchorPosition={contextMenuAnchorPosition}
          onClose={() => setContextMenuAnchorPosition(null)}
          // onSelect is used to determine if the context menu should be shown.
          // onSelect is passed down from pages/components that use the EntitiesSelectionContext
          // so we use it here to determine if this specific row has access to context values or not.
          // TODO: Find a better way to determine if this row has access to context values or not.
          hasEntitiesSelection={!!onSelect}
        />
      )}
    </div>
  );
};

const DraggableRow = ({
  item,
  tableRowVariant,
  hideSelectionColumn,
  enableDrag,
  showDragHandle,
}: {
  item: DraggableRowItem;
  tableRowVariant?: TableRowVariant;
  hideSelectionColumn?: boolean;
  enableDrag?: boolean;
  showDragHandle?: boolean;
}) => {
  const { selectedEntities } = useEntitiesSelection();

  const canDrop = (source: { data: Record<string, unknown> }) => {
    // Don't allow dropping on self
    if (source.data.id === item.row.id) return false;

    // Allow dropping any entity on any other entity
    // This enables reordering spaces, tracks, and files within the same list
    // and also allows moving items into nested spaces
    return true;
  };

  const containerId =
    (item.row as Record<string, unknown>).containerId?.toString?.() ||
    (item.row.parentId as string) ||
    (item.row.parentEntityId as string) ||
    (item.row.spaceId as string) ||
    null;

  // Memoize dragData and dropData to prevent unnecessary useEffect re-runs
  const dragData = useMemo(
    () => ({
      id: item.row.id,
      name: item.row.name,
      type: item.row.type,
      parentId: item.row.parentId || item.row.parentEntityId || null,
      containerId,
    }),
    [
      item.row.id,
      item.row.name,
      item.row.type,
      item.row.parentId,
      item.row.parentEntityId,
      containerId,
    ],
  );

  const dropData = useMemo(
    () => ({
      id: item.row.id,
      containerId,
      type: item.row.type,
    }),
    [item.row.id, containerId, item.row.type],
  );

  return (
    <DnDWrapper
      isDraggable
      isDropTarget
      dragData={dragData}
      entityData={item.row as EntityRowConfig}
      selectedEntities={selectedEntities}
      dropData={dropData}
      canDrop={canDrop}
    >
      <BaseRow
        row={item.row}
        columns={item.columns}
        onSelect={item.onSelect}
        enableDrag={enableDrag}
        showDragHandle={showDragHandle}
        isLastRow={item.isLastRow}
        isFirstRow={item.isFirstRow}
        tableRowVariant={tableRowVariant || item.tableRowVariant}
        hideSelectionColumn={hideSelectionColumn || item.hideSelectionColumn}
      />
    </DnDWrapper>
  );
};

export const BaseTableV2 = ({
  className,
  columns,
  mobileColumns = columns,
  rows,
  EmptyComponent,
  onDragEnd,
  onSelect,
  enableDrag,
  showDragHandle,
  isSelectionEnabled,
  isLoading,
  tableRowVariant,
  hideSelectionColumn = false,
}: {
  className?: string;
  columns: ColumnConfig[];
  mobileColumns?: ColumnConfig[];
  rows: RowConfig[];
  EmptyComponent?: React.FC;
  onDragEnd?: (list: RowConfig[]) => void;
  onSelect?: (row: RowConfig) => void;
  isSelectionEnabled?: boolean;
  enableDrag?: boolean;
  showDragHandle?: boolean;
  isLoading?: boolean;
  tableRowVariant?: TableRowVariant;
  hideSelectionColumn?: boolean;
}) => {
  const { theme } = useTheme();
  const { isMobile } = useViewport();

  const columnsToUse = useMemo(
    () => (isMobile ? mobileColumns : columns),
    [isMobile, columns, mobileColumns],
  );

  if (isLoading) {
    return (
      <div
        className={classNames(
          "highnote-base-table",
          "highnote-base-table-loading-container",
          className,
        )}
      >
        {Array.from({ length: 10 }, (_, i) => {
          return (
            <Skeleton
              key={`highnote-base-table-loading-row-${i}`}
              height={40}
            />
          );
        })}
      </div>
    );
  }

  if (rows.length === 0) {
    if (EmptyComponent)
      return (
        <div className={`highnote-base-table ${className || ""}`}>
          <EmptyComponent />
        </div>
      );

    return null;
  }

  // onDragEnd is now handled by the monitor system

  return (
    <TableContainer
      component={Paper}
      data-theme={theme}
      data-is-selection-enabled={!!isSelectionEnabled}
      className={`highnote-base-table ${className || ""}`}
    >
      <Table
        component="div"
        sx={{ minWidth: 650 }}
        aria-label="table"
        stickyHeader={true}
      >
        <TableHead component="div">
          <TableRow component="div">
            {columnsToUse.map((column) => (
              <TableCell
                component="div"
                key={`column-${column.id}`}
                data-column-id={column.id}
              >
                {column.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody component="div">
          {rows.map((row, i) => {
            const isLastRow = i === rows.length - 1;
            const isFirstRow = i === 0;

            if (enableDrag || onDragEnd) {
              return (
                <DraggableRow
                  key={row.key}
                  item={{
                    row,
                    columns: columnsToUse,
                    isLastRow,
                    isFirstRow,
                    onSelect,
                    hideSelectionColumn,
                  }}
                  tableRowVariant={tableRowVariant}
                  enableDrag={enableDrag}
                  showDragHandle={showDragHandle}
                />
              );
            }

            return (
              <BaseRow
                key={row.key}
                row={row}
                onSelect={onSelect}
                columns={columnsToUse}
                isLastRow={isLastRow}
                isFirstRow={isFirstRow}
                tableRowVariant={tableRowVariant}
                hideSelectionColumn={hideSelectionColumn}
                showDragHandle={showDragHandle}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
